<script>
  import _ from 'lodash'
  import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
  import SearchBox from '@/components/search-box'
  import compoundDetails from '@/components/compound-details'

  export default {
    name: 'compounds',
    props: {
      selectedCompound: {
        type: String,
        required: false
      },
      pageSize: {
        type: Number,
        default: 10
      },
      type: {
        type: String,
        default: ''
      }
    },
    components: {
      'compound-details': compoundDetails,
      'search-box': SearchBox
    },
    computed: {
      ...mapState([
        'cart',
        'compounds',
        'compoundsMap',
        'paginationState',
        'q'
      ]),
      ...mapGetters([ 'getCartId' ]),
      compoundId: function () {
        if (this.selectedCompound) {
          return this.selectedCompound.split(/(-)/, 1)[0]
        }
      },
      compoundName: function () {
        if (this.selectedCompound) {
          return this.selectedCompound.split(/(-)/).slice(2).join('')
        }
      },
      expandedRows () {
        return this.cart.compounds
      }
    },
    mounted () {
      this.initComponent()
    },
    watch: {
      '$route.query.q': function (newVal, oldVal) {
        if (newVal !== oldVal) {
          this.updateQ(newVal)
        }
      },
      '$route.query.page': function (newVal, oldVal) {
        if (newVal !== oldVal) {
          this.setPage(newVal)
        }
      },
      '$route.params.selectedCompound': function (newVal, oldVal) {
        if (newVal !== oldVal) {
          this.initComponent()
        }
      }
    },
    methods: {
      ...mapActions([
        'aFetchCompound',
        'aFetchCompounds',
        'aFetchCartTotal',
        'aFetchCart',
        'aMessageDisplay',
        'aSetPage',
        'aSwitchCart'
      ]),
      ...mapMutations([
        'mSetPage',
        'mUpdateQ'
      ]),
      initComponent () {
        if (this.compoundName) {
          this.$store.commit('mUpdateQ', this.compoundName)
        }
        if (this.$route.query.page) {
          this.setPage(parseInt(this.$route.query.page))
        }
        return this.aFetchCompounds(this.pageSize)
          .then(() => {
            if (this.compoundId) {
              this.expandCompound(this.compoundsMap[this.compoundId])
            }
            if (this.getCartId) {
              return this.aFetchCart()
                .then(() => {
                  this.getExpandedRows()
                })
            }
          })
          .catch((err) => {
            if (err.status === 404 && err.body.detail === 'Niepoprawna strona.') {
              this.setPage(1)
            }
          })
      },
      checkStatus (statusList) {
        return statusList.includes(this.cart.status)
      },

      compoundUrl (row) {
        return window.location.origin + this.$router.resolve({
          name: 'compounds',
          params: {
            selectedCompound: `${row.id}-${row.name}`
          }
        }).href
      },

      getRowClassName (row) {
        return row.row.isAvailable ? '' : 'disabled'
      },
      getRowKey (row) {
        return row.id
      },
      expandCompound (row, expandedRows) {
        if (row && this.compoundsMap[row.id]) {
          if (this.compoundsMap[row.id].details) {
            if (this.$refs.compoundsTable) {
              return this.$refs.compoundsTable.toggleRowExpansion(row, true)
            }
            return
          }
          return this.aFetchCompound(row.id)
            .then(() => {
              this.$refs.compoundsTable.toggleRowExpansion(row, false)
              this.$refs.compoundsTable.toggleRowExpansion(row, true)
            })
        }
      },
      getExpandedRows () {
        this.expandedRows.forEach(id => {
          this.expandCompound({ id })
        })
        return this.cart.compounds
      },
      handleCreateNewCart () {
        this.aSwitchCart()
          .then(() => {})
      },
      toggleExpansion (row, col) {
        this.$refs.compoundsTable.toggleRowExpansion(row)
      },
      setPage (page) {
        this.aSetPage(page)
        this.updateQueryString({ page })
        this.aFetchCompounds(this.pageSize)
          .then(() => {
            this.getExpandedRows()
          })
          .catch((err) => {
            if (err.status === 404 && err.body.detail === 'Niepoprawna strona.') {
              this.setPage(1)
            }
          })
      },
      updateQ: _.debounce(function (q) {
        this.updateQueryString({ q, page: 1 })
        return this.aFetchCompounds(this.pageSize)
      }, 700),
      updateQueryString (update) {
        const query = {
          ...this.$route.query,
          ...update
        }
        this.$router.push({ params: { selectedCompound: null }, query })
      }
    }
  }
</script>

<template>
  <el-main>
    <header>
      <slot>
        <el-row>
          <el-col :span="16" v-if="cart.name">
            <h1>Zamówienie {{cart.name}} ({{ getCartId.slice(0,4) }})</h1>
          </el-col>
          <el-col :span="16" v-else>
            <h1>Wybierz produkty</h1>
          </el-col>
          <el-col :span="8" align="right">
            <el-button
              v-if="cart.name"
              plain
              icon="el-icon-circle-plus-outline"
              type="primary"
              @click="handleCreateNewCart"
            >Rozpocznij nowe zamówienie
            </el-button>
          </el-col>
        </el-row>
      </slot>
    </header>
    <el-row>
      <search-box @input="updateQ"></search-box>
    </el-row>

    <el-table
      ref="compoundsTable"
      :data="compounds"
      :expand-row-keys="expandedRows"
      :row-key="getRowKey"
      :row-class-name="getRowClassName"
      @cell-click="toggleExpansion"
      @expand-change="expandCompound"
    >
      <el-table-column
        v-if="type !== 'simple'"
        type="expand"
      >
        <template slot-scope="props">
          <compound-details
            :compoundId="props.row.id"
            v-if="compoundsMap[props.row.id].details"
          ></compound-details>
        </template>
      </el-table-column>
      <el-table-column label="Nazwa" class-name="clickable">
        <template slot-scope="props">
          <el-col class="name">
            {{ props.row.name }}
            <small v-if="!props.row.isAvailable">(tymczasowo niedostępny)</small>
          </el-col>
        </template>
      </el-table-column>
      <el-table-column
        label="Formuła"
        width="250"
        class-name="clickable"
      >
        <template slot-scope="props">
          {{ props.row.formula }}
        </template>
      </el-table-column>
      <el-table-column
        label="Konsystencja"
        width="150"
        class-name="clickable"
      >
        <template slot-scope="props">
          <el-tag
            type="info"
            size="mini"
          >
            <small>{{ compoundsMap.consistencies[props.row.consistency].name }}</small>
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column
        label="Czystość"
        width="150"
        class-name="clickable"
      >
        <template slot-scope="props">
          <el-tag
            type="info"
            size="mini"
          >
            <small>{{ compoundsMap.purities[props.row.purity].name }}</small>
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column
        label=""
        width="50"
      >
        <template slot-scope="props">
          <el-tooltip class="item" effect="dark"
                      content="Bezpośredni link do produktu"
                      placement="bottom-start">
            <el-link :href="compoundUrl(props.row)">
              <i class="el-icon el-icon-share"></i>
            </el-link>
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>

    <el-row align="start">
      <el-pagination
        v-if="this.type === 'simple'"
        layout="prev, pager, next"
        @current-change="setPage"
        :current-page="paginationState.currenPage"
        :pager-count="5"
        :page-size="pageSize"
        :total="paginationState.recordsFiltered"
      ></el-pagination>
      <el-pagination
        v-else
        layout="prev, pager, next, jumper"
        @current-change="setPage"
        :current-page="paginationState.currenPage"
        :page-size="pageSize"
        :total="paginationState.recordsFiltered"
      ></el-pagination>
    </el-row>
  </el-main>
</template>

<style lang="sass" scoped>
  header
    padding-bottom: 1.5rem

    & .el-button
      margin-top: 2rem
</style>



// WEBPACK FOOTER //
// src/components/compounds.vue